import React, { useEffect, useState, useMemo, useRef } from "react";
import Layout from "@/components/Layout";
import { api, todayISO, toLocalISO } from "@/lib/api";
import { computeRecordHours, buildCCSSDayMap, detectJornadaAndHours } from "@/lib/payroll";
import { buildPuestosMap, puestoNoHorasExtras } from "@/lib/puestos";
import { toast } from "sonner";
import { Clock, Sun, Moon, CloudSun, Coffee, Heart, FileText, Bed, PartyPopper, FileDown, Wand2, Trash2, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { empleadoActivoEn } from "@/lib/frecuencia";
import { feriadoEnFecha } from "@/lib/feriados";
import { getEmpAt } from "@/lib/empleadoVersioning";
import ReportModal from "@/components/ReportModal";
import AdjuntarCsvRelojModal from "@/components/AdjuntarCsvRelojModal";
import { reporteAsistencia } from "@/lib/reportes";
import TimeDropdown from "@/components/TimeDropdown";

const JORNADA_ICON = { Diurna: Sun, Mixta: CloudSun, Nocturna: Moon };
const JORNADA_BADGE = { Diurna: "np-badge-warning", Mixta: "np-badge-info", Nocturna: "np-badge-neutral" };

const ESTADOS = ["Presente", "Día Libre", "Feriado", "Ausente", "Vacaciones", "Licencia", "Incapacidad CCSS", "Incapacidad INS"];

const ESTADO_BADGE_COLOR = {
  "Presente": "np-badge-success",
  "Día Libre": "np-badge-info",
  "Feriado": "np-badge-warning",
  "Ausente": "np-badge-error",
  "Vacaciones": "np-badge-warning",
  "Licencia": "np-badge-warning",
  "Incapacidad CCSS": "np-badge-info",
  "Incapacidad INS": "np-badge-neutral",
};

export default function Asistencia() {
  const [fecha, setFecha] = useState(todayISO());
  const [empleados, setEmpleados] = useState([]);
  const [asist, setAsist] = useState({}); // empleado_id -> registro
  const [allCCSS, setAllCCSS] = useState([]); // todos los registros CCSS para el día-de-período
  const [showReport, setShowReport] = useState(false);
  const [showRelojCsv, setShowRelojCsv] = useState(false);
  const [empresa, setEmpresa] = useState({});
  const [empleadosAll, setEmpleadosAll] = useState([]);

  // Refs para prevenir race conditions al escribir horas y cambiar de fecha rápido.
  // - asistRef: mirror sincrónico de `asist` (para que el debounce lea la última versión mergeada).
  // - fechaRef: mirror sincrónico de `fecha` (para descartar respuestas HTTP que llegan tarde y pertenecen a un día distinto al actual).
  // - timersRef: timeouts pendientes por empleado (para debouncing y flush).
  const asistRef = useRef({});
  const fechaRef = useRef(fecha);
  const timersRef = useRef({});
  useEffect(() => { asistRef.current = asist; }, [asist]);
  useEffect(() => { fechaRef.current = fecha; }, [fecha]);

  useEffect(() => {
    api.get("/empresa").then(r => setEmpresa(r.data || {})).catch(() => {});
  }, []);

  const generarReporteAsistencia = async ({ format, fechaInicio, fechaFin }) => {
    const { data } = await api.get(`/asistencia?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
    reporteAsistencia({ format, asistencias: data || [], empleados, fechaInicio, fechaFin, empresa });
  };

  const normalizarHoras = async () => {
    const ok = window.confirm(
      "Esta acción redondeará TODOS los minutos raros de asistencia " +
      "(ej. 14:01 → 14:00, 14:03 → 14:05) al múltiplo de 5 más cercano en toda la historia. " +
      "\n\n¿Continuar?"
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/asistencia/normalizar");
      toast.success(
        `Normalización completa. Escaneados: ${data.scanned}. Corregidos: ${data.fixed}.`,
        { duration: 8000 }
      );
      // Recargar la vista actual por si algún registro del día visible cambió.
      await load();
    } catch (e) {
      toast.error("No se pudo normalizar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const limpiarDuplicados = async () => {
    const ok = window.confirm(
      "Esta acción elimina registros DUPLICADOS de asistencia (mismo empleado + misma fecha). " +
      "Se conserva el más completo. \n\n¿Continuar?"
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/asistencia/dedup");
      toast.success(
        `Limpieza completa. Grupos con duplicados: ${data.duplicate_groups}. Registros eliminados: ${data.removed}.`,
        { duration: 8000 }
      );
      await load();
    } catch (e) {
      toast.error("No se pudo limpiar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const load = async () => {
    const [e, a, allA] = await Promise.all([
      api.get("/empleados"),
      api.get(`/asistencia?fecha_inicio=${fecha}&fecha_fin=${fecha}`),
      // Cargar 90 días previos para calcular el día-de-período de CCSS
      api.get(`/asistencia?fecha_inicio=${isoMinus(fecha, 90)}&fecha_fin=${fecha}`),
    ]);
    setEmpleadosAll(e.data || []);
    setEmpleados(e.data.filter(x => x.estado === "Activo" && empleadoActivoEn(x, fecha)));
    const map = {};
    a.data.forEach(r => { map[r.empleado_id] = r; });
    setAsist(map);
    setAllCCSS(allA.data.filter(r => r.estado === "Incapacidad CCSS"));
  };

  useEffect(() => { load(); }, [fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  // ccssDayMap reactivo (incluye los registros actuales optimistas + historial)
  const ccssDayMap = useMemo(() => {
    const merged = [...allCCSS];
    Object.values(asist).forEach(r => {
      if (r.estado === "Incapacidad CCSS" && r.fecha) {
        // Reemplazar entrada existente para este registro
        const idx = merged.findIndex(x => x.empleado_id === r.empleado_id && x.fecha === r.fecha);
        if (idx >= 0) merged[idx] = r; else merged.push(r);
      }
    });
    return buildCCSSDayMap(merged);
  }, [asist, allCCSS]);

  // Puestos configurados como "sin horas extras" (Art. 143 CT).
  const puestosMap = useMemo(() => buildPuestosMap(empresa), [empresa]);
  const isNoExtras = (emp) => puestoNoHorasExtras(puestosMap, emp?.puesto);

  // Persistencia real (POST) con descarte de respuestas desfasadas.
  const persistRecord = async (empId, record, fechaSnap) => {
    try {
      const { data } = await api.post("/asistencia", record);
      // Si el usuario ya cambió de día, NO tocamos la UI para no pisar datos del nuevo día.
      if (fechaRef.current === fechaSnap) {
        setAsist(prev => ({ ...prev, [empId]: data }));
      }
    } catch (e) { /* ignore */ }
  };

  // Flush inmediato de todos los timers pendientes (se usa al cambiar de fecha o desmontar).
  const flushAllTimers = () => {
    const timers = timersRef.current;
    Object.keys(timers).forEach(k => {
      const t = timers[k];
      if (t) {
        clearTimeout(t.timeoutId);
        try { t.save(); } catch {}
      }
    });
    timersRef.current = {};
  };

  // Antes de cambiar de fecha, flush a los timers pendientes con la fecha capturada.
  useEffect(() => {
    return () => { flushAllTimers(); };
  }, [fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  const upsert = (emp, patch, opts = {}) => {
    const fechaSnap = fechaRef.current;
    const current = asistRef.current[emp.id] || { empleado_id: emp.id, fecha: fechaSnap, estado: "Presente" };
    const updated = { ...current, ...patch, empleado_id: emp.id, fecha: fechaSnap };

    // Si el estado NO es "Presente" ni "Feriado" → limpiar entrada/salida (no aplican)
    if (updated.estado !== "Presente" && updated.estado !== "Feriado") {
      updated.entrada = "";
      updated.salida = "";
    }

    // Aviso si la hora de salida es MENOR o IGUAL que la de entrada
    if (updated.entrada && updated.salida && (updated.estado === "Presente" || updated.estado === "Feriado")) {
      const [eh, em] = updated.entrada.split(":").map(Number);
      const [sh, sm] = updated.salida.split(":").map(Number);
      const startMin = eh * 60 + em;
      const endMin = sh * 60 + sm;
      if (endMin <= startMin) {
        toast.warning(
          `${emp.nombre}: la hora de salida (${updated.salida}) es menor o igual que la entrada (${updated.entrada}). Se calculará como jornada nocturna hasta el día siguiente. Verificá si es correcto.`,
          { duration: 6000 }
        );
      }
    }

    // Calcular horas según estado (computeRecordHours ya resuelve versioning por fecha)
    const calc = computeRecordHours(updated, emp, ccssDayMap);
    const empAt = getEmpAt(emp, updated.fecha);
    const extras = puestoNoHorasExtras(puestosMap, empAt?.puesto) ? 0 : (calc.ext || 0);
    updated.jornada = calc.jornada || null;
    updated.horas_ordinarias = calc.ord || 0;
    updated.horas_extras = extras;

    // Actualización optimista de UI (siempre inmediata para que el input responda).
    setAsist(prev => ({ ...prev, [emp.id]: updated }));
    asistRef.current = { ...asistRef.current, [emp.id]: updated };

    const key = emp.id;
    // Cancelar timer previo para este empleado (si estaba escribiendo horas).
    if (timersRef.current[key]) {
      clearTimeout(timersRef.current[key].timeoutId);
      delete timersRef.current[key];
    }

    // Función de save que captura fecha en snapshot (para no pisar el día equivocado).
    const save = () => {
      delete timersRef.current[key];
      const latest = asistRef.current[emp.id];
      if (latest) persistRecord(emp.id, { ...latest, empleado_id: emp.id, fecha: fechaSnap }, fechaSnap);
    };

    // opts.local: sólo actualiza UI en local, NO programa POST (usado por onChange de inputs time
    //   para no persistir valores intermedios mientras el usuario aún está escribiendo).
    // opts.immediate: guarda inmediato (usado por onBlur, cambio de estado, botones).
    // Por defecto: debounce como red de seguridad de 1500ms.
    if (opts.local) {
      return;
    }
    if (opts.immediate) {
      save();
    } else {
      timersRef.current[key] = { timeoutId: setTimeout(save, 1500), save };
    }
  };

  const totals = useMemo(() => {
    let ord = 0, ext = 0, presentes = 0, ccssPagas = 0, ccssNoPagas = 0;
    Object.values(asist).forEach(a => {
      const emp = empleados.find(e => e.id === a.empleado_id);
      const empAt = emp ? getEmpAt(emp, a.fecha) : null;
      const h = computeRecordHours(a, emp, ccssDayMap);
      ord += h.ord || 0;
      // Excluir extras si el puesto del empleado está exento (Art. 143 CT) — resuelto a la fecha
      if (!puestoNoHorasExtras(puestosMap, empAt?.puesto)) ext += h.ext || 0;
      if (a.estado === "Presente" && a.entrada && a.salida) presentes++;
      if (a.estado === "Incapacidad CCSS") {
        if (h.paid) ccssPagas++; else ccssNoPagas++;
      }
    });
    return { ord, ext, presentes, ccssPagas, ccssNoPagas };
  }, [asist, empleados, ccssDayMap, puestosMap]);

  const feriadoHoy = useMemo(() => feriadoEnFecha(fecha), [fecha]);

  const marcarTodosFeriado = async () => {
    for (const emp of empleados) {
      const current = asist[emp.id] || { empleado_id: emp.id, fecha, estado: "Presente" };
      if (current.estado === "Feriado") continue;
      upsert(emp, { estado: "Feriado" }, { immediate: true });
    }
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Clock size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Asistencia</h1>
            <p className="text-sm text-secondary-c">Control de entrada y salida</p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {empresa?.reloj_marcador_csv_enabled && (
            <button
              onClick={() => setShowRelojCsv(true)}
              className="np-btn np-btn-outline"
              title="Adjuntar CSV exportado desde un reloj marcador"
              data-testid="asistencia-reloj-csv-btn"
            >
              <Upload size={16} className="mr-1.5" />Adjuntar CSV
            </button>
          )}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                const ae = document.activeElement;
                if (ae && ae.tagName === "INPUT" && ae.type === "time") ae.blur();
                setFecha(isoMinus(fecha, 1));
              }}
              className="np-btn np-btn-outline p-2"
              title="Día anterior"
              data-testid="asistencia-date-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <input type="date" className="np-input" value={fecha}
              onChange={e => {
                // Si hay un input type=time con foco, forzar blur para disparar su persistencia
                // ANTES de cambiar de fecha (evita perder digitación pendiente).
                const ae = document.activeElement;
                if (ae && ae.tagName === "INPUT" && ae.type === "time") ae.blur();
                setFecha(e.target.value);
              }}
              data-testid="asistencia-date" />
            <button
              onClick={() => {
                const ae = document.activeElement;
                if (ae && ae.tagName === "INPUT" && ae.type === "time") ae.blur();
                setFecha(isoMinus(fecha, -1));
              }}
              className="np-btn np-btn-outline p-2"
              title="Día siguiente"
              data-testid="asistencia-date-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="asistencia-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={normalizarHoras} className="np-btn np-btn-outline" title="Redondea minutos raros (ej 14:01) al múltiplo de 5 más cercano en TODA la historia" data-testid="asistencia-normalize-btn"><Wand2 size={16} className="mr-1.5" />Normalizar horas</button>
          <button onClick={limpiarDuplicados} className="np-btn np-btn-outline" title="Elimina registros duplicados (mismo empleado + misma fecha), conserva el más completo" data-testid="asistencia-dedup-btn"><Trash2 size={16} className="mr-1.5" />Limpiar duplicados</button>
          <div className="flex gap-2 text-sm flex-wrap">
            <div className="np-badge np-badge-info" data-testid="presentes-count">Presentes: {totals.presentes}</div>
            <div className="np-badge np-badge-success">Ord: {totals.ord.toFixed(1)}h</div>
            <div className="np-badge np-badge-warning">Ext: {totals.ext.toFixed(1)}h</div>
          </div>
        </div>
      </header>

      {showReport && (
        <ReportModal
          title="Reporte de Asistencia" mode="rango"
          onClose={() => setShowReport(false)}
          onGenerate={generarReporteAsistencia}
        />
      )}

      {showRelojCsv && (
        <AdjuntarCsvRelojModal
          empleados={empleadosAll.filter(x => x.estado === "Activo")}
          onClose={() => setShowRelojCsv(false)}
          onImported={() => { load(); }}
        />
      )}

      {feriadoHoy && (
        <div className="np-card mb-5 flex items-center gap-4 flex-wrap" style={{ background: "linear-gradient(135deg, #FFF4E5 0%, #FDF1EC 100%)", borderColor: "#F2DFB6" }} data-testid="feriado-banner">
          <div className="w-12 h-12 rounded-2xl bg-[#D96C4A] text-white flex items-center justify-center flex-shrink-0">
            <PartyPopper size={22} strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-[#A67115]">{feriadoHoy.nombre} · Feriado oficial</div>
            <div className="text-xs text-secondary-c">El trabajo realizado este día se paga al doble. Marcá a los empleados que efectivamente trabajaron.</div>
          </div>
          <button onClick={marcarTodosFeriado} className="np-btn np-btn-accent" data-testid="marcar-todos-feriado">
            <PartyPopper size={16} /> Marcar a todos como Feriado
          </button>
        </div>
      )}

      <div className="np-card p-0 np-scroll">
        <table className="np-table">
          <thead><tr>
            <th>Empleado</th>
            <th>Entrada</th>
            <th>Salida</th>
            <th>Jornada</th>
            <th>Almuerzo</th>
            <th>Ordinarias</th>
            <th>Extras</th>
            <th>Total</th>
            <th>Estado</th>
          </tr></thead>
          <tbody>
            {empleados.map(emp => {
              const r = asist[emp.id] || {};
              // Resuelve campos versionados (almuerzo_paga, jornada, etc.) a la fecha mostrada,
              // para que cambios futuros no afecten el histórico y viceversa.
              const empAt = getEmpAt(emp, fecha);
              const isPresente = (r.estado || "Presente") === "Presente";
              const isFeriado = r.estado === "Feriado";
              const canTimeEntry = isPresente || isFeriado;
              // Para mostrar valores
              const calcRaw = computeRecordHours({ ...r, empleado_id: emp.id, fecha }, emp, ccssDayMap);
              // Puestos sin horas extras (Art. 143 CT): no mostrar extras.
              const noExt = isNoExtras(empAt);
              const calc = noExt ? { ...calcRaw, ext: 0 } : calcRaw;
              const jornada = canTimeEntry ? (detectJornadaAndHours(r.entrada, r.salida, empAt.almuerzo_paga).jornada) : calc.jornada;
              const Ic = jornada ? (JORNADA_ICON[jornada] || Sun) : null;
              const tot = (calc.ord || 0) + (calc.ext || 0);
              const reclasificada = isPresente && detectJornadaAndHours(r.entrada, r.salida, empAt.almuerzo_paga).reclasificada;
              // Jornada nocturna probable: si la entrada cae en zona nocturna (>=19:00 o <5:00),
              // se permite salida "menor" (cruza medianoche). Ejemplo: 22:00 → 06:00.
              const entradaH = (r.entrada || "").split(":")[0];
              const isEntradaNocturna = entradaH !== "" && (Number(entradaH) >= 19 || Number(entradaH) < 5);
              return (
                <tr key={emp.id} data-testid={`asist-row-${emp.cedula}`}>
                  <td>
                    <div className="font-medium">{emp.nombre} {emp.apellidos}</div>
                    <div className="text-xs text-secondary-c">{empAt.puesto}</div>
                  </td>
                  <td>
                    <TimeDropdown
                      value={r.entrada || ""}
                      disabled={!canTimeEntry}
                      onChange={(v) => upsert(emp, { entrada: v }, { immediate: true })}
                      testId={`entrada-${emp.cedula}`}
                    />
                  </td>
                  <td>
                    <TimeDropdown
                      value={r.salida || ""}
                      disabled={!canTimeEntry}
                      onChange={(v) => upsert(emp, { salida: v }, { immediate: true })}
                      minTime={r.entrada || ""}
                      allowBefore={isEntradaNocturna}
                      testId={`salida-${emp.cedula}`}
                    />
                  </td>
                  <td>
                    {jornada ? (
                      <div className="flex flex-col gap-1" data-testid={`jornada-${emp.cedula}`}>
                        <span className={`np-badge ${JORNADA_BADGE[jornada]} flex items-center gap-1 w-fit`}><Ic size={12} /> {jornada}</span>
                        <span className="text-[10px] text-secondary-c">
                          máx {calc.ordLimit || (jornada === "Nocturna" ? 6 : jornada === "Mixta" ? 7 : 8)}h ord.
                          {reclasificada && <span className="ml-1 text-warning font-medium" title="Reclasificada por Art. 138"> · Art. 138</span>}
                        </span>
                      </div>
                    ) : <span className="text-secondary-c">—</span>}
                  </td>
                  <td className="text-xs text-secondary-c">{canTimeEntry ? (empAt.almuerzo_paga ? "Pago" : "−1h") : "—"}</td>
                  <td className="font-medium" data-testid={`ord-${emp.cedula}`}>
                    {calc.ord ? `${calc.ord}h` : "—"}
                    {r.estado === "Incapacidad CCSS" && calc.ccssDay && (
                      <div className="text-[10px] text-secondary-c">día {calc.ccssDay}/3 {calc.paid ? "pago" : "no pago"}</div>
                    )}
                  </td>
                  <td className={calc.ext > 0 ? "text-warning font-medium" : ""} data-testid={`ext-${emp.cedula}`}>{calc.ext ? `${calc.ext}h` : "—"}</td>
                  <td className="font-semibold">{tot ? `${tot.toFixed(1)}h` : "—"}</td>
                  <td>
                    <select className="np-input py-1 px-2 text-xs w-40" value={r.estado || "Presente"} onChange={e => upsert(emp, { estado: e.target.value }, { immediate: true })} data-testid={`estado-${emp.cedula}`}>
                      {ESTADOS.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="np-card text-xs text-secondary-c flex items-start gap-3">
          <Sun size={14} className="mt-0.5 text-warning flex-shrink-0" />
          <div>
            <div className="font-medium text-primary-c mb-1">Detección automática de jornada</div>
            <b>Diurna</b> 05:00–19:00 (máx 8 h) · <b>Nocturna</b> 19:00–05:00 (máx 6 h) · <b>Mixta</b> ambas franjas (máx 7 h).
            <div className="mt-1.5"><b>Art. 138 CT</b> (con 5 min de gracia): si la jornada mixta incluye <b>3h35 o más</b> en horario nocturno → se reclasifica a Nocturna. Antes de 22:35 (saliendo desde el inicio de la zona nocturna) se mantiene Mixta.</div>
          </div>
        </div>
        <div className="np-card text-xs text-secondary-c flex items-start gap-3">
          <Heart size={14} className="mt-0.5 text-error flex-shrink-0" />
          <div>
            <div className="font-medium text-primary-c mb-1">Reglas por estado (pago al empleado)</div>
            <div className="flex flex-col gap-1">
              <span className="flex items-start gap-1.5"><Coffee size={11} className="text-success mt-0.5 flex-shrink-0" /> <span><b>Día Libre / Vacaciones</b>: 8 h pagas al 100 % por el patrono.</span></span>
              <span className="flex items-start gap-1.5"><Heart size={11} className="text-warning mt-0.5 flex-shrink-0" /> <span><b>Licencia (Art. 95 CT)</b>: el patrono paga el 50 % del salario diario (4 h) y la CCSS aporta el otro 50 % directamente al empleado, durante todo el período de la licencia.</span></span>
              <span className="flex items-start gap-1.5"><Bed size={11} className="text-error mt-0.5 flex-shrink-0" /> <span><b>Ausente / Inc. INS</b>: 0 h (el INS paga directamente al empleado).</span></span>
              <span className="flex items-start gap-1.5"><FileText size={11} className="text-primary-color mt-0.5 flex-shrink-0" /> <span><b>Inc. CCSS</b>: los 3 primeros días el patrono paga 50 % (4 h c/u); del 4.° día en adelante 0 h (CCSS asume el subsidio).</span></span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function isoMinus(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() - days);
  return toLocalISO(d);
}
